import { Component, OnInit, AfterViewInit, Input, ViewChild, ElementRef, OnDestroy, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { NGXLogger } from 'ngx-logger';
import * as maplibregl from 'maplibre-gl';
import { ComuniService } from '../../services/comuni.service';
import { ComuneFeature, ComuniFeatureCollection, ComuneProperties } from '../../models/comune-geojson.model';
import { Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { RichiestaStats } from '../../models/richiesta-stats.model';
import { RichiesteService } from '../../services/richieste.service';
import { EntiEsecutoriService } from '../../services/entiEsecutori.service';
import { environment } from '../../../environments/environment';

// Estendi l'interfaccia per includere la proprietà fillColor
interface ComunePropertiesWithColor extends ComuneProperties {
  fillColor?: string;
  statoColore?: string;
}

// Interfaccia per i dati dei consorzi
interface ConsorzioInfo {
  nome: string;
  coordinate: [number, number]; // [longitudine, latitudine] - formato richiesto da MapLibre
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    TooltipModule,
    ToastModule
  ],
  providers: [MessageService]
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() isPresidioTIM: boolean = false;
  @ViewChild('map') mapContainer!: ElementRef;
  @ViewChild('mapTooltip') mapTooltipEl!: ElementRef;

  statsRichieste: RichiestaStats = {
    anno: new Date().getFullYear(),
    conteggioPerStato: {},
    importoRichiestoPerStato: {},
    importoFinanziatoPerStato: {},
    importoRendicontatoPerStato: {},
    importoContributoPerStato: {},
    totaleRichieste: 0,
    totaleImportoRichiesto: 0,
    totaleImportoFinanziato: 0,
    totaleImportoRendicontato: 0,
    totaleImportoContributo: 0
  };

  annoCorrente: number = (() => {
    const dataCorrente = new Date();
    const meseCorrente = dataCorrente.getMonth() + 1; // getMonth() restituisce 0-11, aggiungiamo 1 per avere 1-12
    return meseCorrente <= 6 ? dataCorrente.getFullYear() : dataCorrente.getFullYear() + 1;
  })();
  private map: maplibregl.Map | null = null;
  private comuniSubscription?: Subscription;
  private comuniData?: ComuniFeatureCollection;
  private popupAttivo?: maplibregl.Popup;
  private tooltipVisible = false;
  private markers: maplibregl.Marker[] = []; // Array per tenere traccia dei marker
  public isTestEnvironment: boolean = false;

  // Dati dei consorzi
  private consorzi: ConsorzioInfo[] = [
    {
      nome: 'Consorzio Bealera Caselette',
      coordinate: [7.42, 45.12] // Coordinate arrotondate e leggermente modificate
    },
    {
      nome: 'Consorzio Forestale Villarfocchiardo',
      coordinate: [7.22, 45.13] // Coordinate arrotondate e leggermente modificate
    },
     {
      nome: 'Consorzio irriguo Valli Susa e Cenischia',
      coordinate: [7.13, 45.145] // Coordinate arrotondate e leggermente modificate
    }
  ];


  constructor(
    private router: Router,
    private logger: NGXLogger,
    private comuniService: ComuniService,
    private renderer: Renderer2,
    private messageService: MessageService,
    private richiesteService: RichiesteService,
    private entiEsecutoriService: EntiEsecutoriService
  ) {}

  ngOnInit() {
    // Controlla se siamo in ambiente di test o dev
    this.isTestEnvironment = environment.name === 'TEST' || environment.name === 'DEV';
    
    this.loadStats();
    this.loadComuniData();
  }

  ngAfterViewInit() {
    // Inizializza la mappa
    this.initializeMap();
  }

    loadStats() {

    this.richiesteService.getStatsRichieste(this.annoCorrente).subscribe(stats => {
      this.statsRichieste = stats;
      ////this.logger.info('[DASHBOARD] Statistiche richieste caricate', this.statsRichieste);
    });

    // Simulazione di caricamento dati - da implementare con chiamata al servizio
  /*   this.statsRichieste = {
      inPreparazione: 32,
      presentate: 45,
      finanziate: 28,
      rendicontate: 15
    };
    ////this.logger.info('[DASHBOARD] Statistiche richieste caricate', this.statsRichieste); */
  }

  loadComuniData() {
    this.comuniSubscription = this.comuniService.getConfiniComuniPerAnno(this.annoCorrente).subscribe({
      next: (data) => {
        this.comuniData = data;
        ////this.logger.info('[DASHBOARD] Dati comuni caricati', data.features.length);

        // Se la mappa è già inizializzata, aggiungiamo i confini
        if (this.map) {
          this.aggiungiConfiniComuni();
        }
      },
      error: (err) => {
        this.logger.error('[DASHBOARD] Errore caricamento dati comuni', err);
      }
    });
  }

  apriInterventiComune(codiceCatastale: string, denominazione: string) {
    this.router.navigate(['/interventi'], { queryParams: { codiceCatastale: codiceCatastale, denominazione: denominazione } });
  }

  apriInterventiConsorzio(consorzioDB: any) {
    this.router.navigate(['/interventi'], { queryParams: { consorzio: consorzioDB.id, denominazione: consorzioDB.label } });
  }

  visualizzaRichieste(stato: string) {


    if (stato === 'tutte') {
      // Navigazione verso tutte le richieste senza filtro per stato
      ////this.logger.info('[DASHBOARD] Visualizzazione di tutte le richieste dell\'anno corrente');
      this.router.navigate(['/richieste'], { queryParams: { anno: this.annoCorrente } });
    } else {
      //this.logger.info(`[DASHBOARD] Visualizzazione richieste in stato: ${stato}`);
      // Navigazione verso le richieste filtrate per stato
      this.router.navigate(['/richieste'], { queryParams: { stato: stato, anno: this.annoCorrente } });
    }
  }

  // Metodi per la gestione della mappa
  private initializeMap(): void {
    // Configurazione della mappa - Valle di Susa (Coordinate approssimative)
    const initialState = {
      lng: 7.200,  // Longitudine Valle di Susa - spostata più a sinistra (ovest)
      lat: 45.1381, // Latitudine Valle di Susa (Susa)
      zoom: 9.8     // Livello di zoom per vedere tutta la valle
    };

    setTimeout(() => {
      if (this.mapContainer && this.mapContainer.nativeElement) {
        try {
          // Configurazione per lo stile della mappa con OpenStreetMap
          this.map = new maplibregl.Map({
            container: this.mapContainer.nativeElement,
            style: {
              version: 8 as 8, // Per soddisfare il type checking di StyleSpecification
              sources: {
                'osm-tiles': {
                  type: 'raster',
                  tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
                  tileSize: 256,
                }
              },
              layers: [
                {
                  id: 'osm-tiles',
                  type: 'raster',
                  source: 'osm-tiles',
                  minzoom: 0,
                  maxzoom: 19
                }
              ]
            },
            center: [initialState.lng, initialState.lat],
            zoom: initialState.zoom
          });

          // Evento di caricamento completato
          this.map.on('load', () => {
            ////this.logger.info('[DASHBOARD] Mappa caricata con successo');

            // Sequenza di inizializzazione:
            // 1. Prima aggiungiamo i confini dei comuni
            if (this.comuniData) {
              this.aggiungiConfiniComuni();
            }

            // 2. Poi aggiungiamo i marker dei consorzi
            this.aggiungiMarkerConsorzi();

            // 3. Aggiungiamo il marker speciale
            this.aggiungiMarkerUMVS();
          });

          // Gestione degli errori
          this.map.on('error', (error: any) => {
            this.logger.error('[DASHBOARD] Errore nella mappa:', error);
          });
        } catch (error) {
          this.logger.error('[DASHBOARD] Errore durante l\'inizializzazione della mappa:', error);
        }
      } else {
        this.logger.error('[DASHBOARD] Elemento mappa non trovato nel DOM');
      }
    }, 500);
  }

  private aggiungiMarkerConsorzi(): void {
    ////this.logger.info('[DASHBOARD] Aggiungi marker consorzi');
    //this.entiEsecutoriService.getStatsConsorzi().subscribe(consorziDaDB => {
    this.entiEsecutoriService.getStatsConsorziPerAnno(this.annoCorrente).subscribe(consorziDaDB => {
      ////this.logger.info('[DASHBOARD] Dati consorzi recuperati', consorziDaDB);
      ////this.logger.info('[DASHBOARD] Consorzi predefiniti', this.consorzi);

      // Incrocia i dati dal DB con la lista dei consorzi predefinita
      consorziDaDB.forEach(consorzioDB => {
        const consorzioPredefinito = this.consorzi.find(c => c.nome === consorzioDB.label);

        if (consorzioPredefinito) {
          // Determina il colore in base allo stato
          const colore = this.getColoreByStato(consorzioDB.statoRichiesta || 'IN PREPARAZIONE');
          ////this.logger.info('[DASHBOARD] Colore del consorzio', colore);

          // Crea elemento HTML per l'icona PrimeNG
          const el = document.createElement('div');
          el.className = 'consorzio-marker';
          el.innerHTML = `<i class="pi pi-map-marker" style="font-size: 40px; color: ${colore}; text-shadow: 2px 2px 4px rgba(0,0,0,0.3);"></i>`;
         // el.innerHTML = `<span class="material-icons" style="font-size: 40px; color:${colore}; text-shadow: 2px 2px 4px rgba(0,0,0,0.3);">group_work</span>`;

          // Stile del marker
          el.style.cursor = 'pointer';
          el.style.width = '45px';
          el.style.height = '45px';
          el.style.display = 'flex';
          el.style.justifyContent = 'center';
          el.style.alignItems = 'center';
          el.style.zIndex = '2000';
          el.style.pointerEvents = 'auto';
          el.style.filter = 'drop-shadow(0px 2px 4px rgba(0,0,0,0.3))';
          el.style.transition = 'transform 0.2s ease-in-out';

          // Aggiungi eventi per il tooltip
          el.addEventListener('mouseenter', (e) => {
            if (!this.map || !this.mapTooltipEl) return;

            // Nascondi il tooltip dei comuni
            this.renderer.removeClass(this.mapTooltipEl.nativeElement, 'visible');
            this.tooltipVisible = false;

            // Aggiorna contenuto del tooltip
            const tooltipEl = this.mapTooltipEl.nativeElement;
            const titleEl = tooltipEl.querySelector('#tooltip-title');
            const statoEl = tooltipEl.querySelector('#tooltip-stato');

            if (titleEl && statoEl) {
              titleEl.textContent = consorzioDB.label;
              statoEl.textContent = consorzioDB.statoRichiesta || 'In preparazione';
              statoEl.style.color = colore;
            }

            // Mostra il tooltip
            this.renderer.addClass(tooltipEl, 'visible');
            this.tooltipVisible = true;

            // Aggiorna la posizione del tooltip
            const rect = el.getBoundingClientRect();
            const mapRect = this.mapContainer.nativeElement.getBoundingClientRect();
            const x = rect.left - mapRect.left + (rect.width / 2);
            const y = rect.top - mapRect.top;

            this.aggiornaPosizioneTooltip({
              x: x,
              y: y
            });

            // Previeni la propagazione dell'evento
            e.stopPropagation();
          });

          el.addEventListener('mousemove', (e) => {
            if (!this.map || !this.mapTooltipEl || !this.tooltipVisible) return;

            const rect = el.getBoundingClientRect();
            const mapRect = this.mapContainer.nativeElement.getBoundingClientRect();
            const x = rect.left - mapRect.left + (rect.width / 2);
            const y = rect.top - mapRect.top;

            this.aggiornaPosizioneTooltip({
              x: x,
              y: y
            });

            // Previeni la propagazione dell'evento
            e.stopPropagation();
          });

          el.addEventListener('mouseleave', (e) => {
            if (!this.map || !this.mapTooltipEl) return;
            this.renderer.removeClass(this.mapTooltipEl.nativeElement, 'visible');
            this.tooltipVisible = false;

            // Previeni la propagazione dell'evento
            e.stopPropagation();
          });

          // Registra l'evento di click
          el.addEventListener('click', (e) => {
            // Previeni la propagazione dell'evento al layer dei comuni
            e.stopPropagation();


            this.apriInterventiConsorzio(consorzioDB);
          });

          // Crea e aggiungi il marker alla mappa
          const marker = new maplibregl.Marker({
            element: el,
            anchor: 'bottom',
            offset: [0, 0]
          })
          .setLngLat(consorzioPredefinito.coordinate)
          .addTo(this.map as any);

          // Aggiungi il marker all'array per la pulizia futura
          this.markers.push(marker);

          //this.logger.info(`[DASHBOARD] Marker aggiunto per ${consorzioDB.label} alle coordinate [${consorzioPredefinito.coordinate[0]}, ${consorzioPredefinito.coordinate[1]}]`);
        }
      });
    });
  }

  private aggiungiMarkerUMVS(): void {
    if (!this.map) return;

    this.entiEsecutoriService.getStatsUMVSPerAnno(this.annoCorrente).subscribe(umvsDaDB => {
      ////this.logger.info('[DASHBOARD] Dati UMVS recuperati', umvsDaDB);
      const colore = this.getColoreByStato(umvsDaDB.statoRichiesta || 'IN PREPARAZIONE');
      ////this.logger.info('[DASHBOARD] Colore del consorzio', colore);
      // Crea elemento HTML per l'icona speciale
      const el = document.createElement('div');
      el.className = 'marker-speciale';
      el.innerHTML = `<span class="material-icons" style="font-size: 30px; color: ${colore}; text-shadow: 2px 2px 4px rgba(0,0,0,0.3);">hub</span>`;

        // Stile del marker
        el.style.cursor = 'pointer';
        el.style.width = '20px';
        el.style.height = '20px';
        el.style.display = 'flex';
        el.style.justifyContent = 'center';
        el.style.alignItems = 'center';
        el.style.zIndex = '2100'; // Più alto dei marker dei consorzi
        el.style.pointerEvents = 'auto';
        el.style.filter = 'drop-shadow(0px 2px 4px rgba(0,0,0,0.3))';
        el.style.transition = 'transform 0.2s ease-in-out';

        // Aggiungi eventi per il tooltip
        el.addEventListener('mouseenter', (e) => {
          if (!this.map || !this.mapTooltipEl) return;

          // Nascondi il tooltip dei comuni
          this.renderer.removeClass(this.mapTooltipEl.nativeElement, 'visible');
          this.tooltipVisible = false;

          // Aggiorna contenuto del tooltip
          const tooltipEl = this.mapTooltipEl.nativeElement;
          const titleEl = tooltipEl.querySelector('#tooltip-title');
          const statoEl = tooltipEl.querySelector('#tooltip-stato');

          if (titleEl && statoEl) {
            titleEl.textContent = 'UMVS';
            statoEl.textContent = umvsDaDB.statoRichiesta || 'In preparazione';
            statoEl.style.color = colore;
          }

          // Mostra il tooltip
          this.renderer.addClass(tooltipEl, 'visible');
          this.tooltipVisible = true;

          // Aggiorna la posizione del tooltip
          const rect = el.getBoundingClientRect();
          const mapRect = this.mapContainer.nativeElement.getBoundingClientRect();
          const x = rect.left - mapRect.left + (rect.width / 2);
          const y = rect.top - mapRect.top;

          this.aggiornaPosizioneTooltip({
            x: x,
            y: y
          });

          // Previeni la propagazione dell'evento
          e.stopPropagation();
        });

        el.addEventListener('mousemove', (e) => {
          if (!this.map || !this.mapTooltipEl || !this.tooltipVisible) return;

          const rect = el.getBoundingClientRect();
          const mapRect = this.mapContainer.nativeElement.getBoundingClientRect();
          const x = rect.left - mapRect.left + (rect.width / 2);
          const y = rect.top - mapRect.top;

          this.aggiornaPosizioneTooltip({
            x: x,
            y: y
          });

          // Previeni la propagazione dell'evento
          e.stopPropagation();
        });

        el.addEventListener('mouseleave', (e) => {
          if (!this.map || !this.mapTooltipEl) return;
          this.renderer.removeClass(this.mapTooltipEl.nativeElement, 'visible');
          this.tooltipVisible = false;

          // Previeni la propagazione dell'evento
          e.stopPropagation();
        });

        // Registra l'evento di click
        el.addEventListener('click', (e) => {
          // Previeni la propagazione dell'evento
          e.stopPropagation();

          // Mostra un messaggio o naviga verso una pagina specifica
          this.apriInterventiConsorzio(umvsDaDB);
        });

        // Crea e aggiungi il marker alla mappa
        const marker = new maplibregl.Marker({
          element: el,
          anchor: 'center',
          offset: [0, 0]
        })
        .setLngLat([7.138624, 45.1373092])
        .addTo(this.map as any);

        // Aggiungi il marker all'array per la pulizia futura
        this.markers.push(marker);
    });
  }

  private aggiungiConfiniComuni(): void {
    if (!this.map || !this.comuniData) return;

    try {
      // Aggiungi la fonte dei dati GeoJSON alla mappa
      if (!(this.map as any).getSource('comuni-source')) {

        // Genera colori per ogni comune basati sui 4 stati
        const features = this.comuniData.features.map((feature, index) => {
          // Clona la feature per non modificare l'originale
          const newFeature = { ...feature };

          // Assegna il colore in base allo stato della richiesta
          const colore = this.getColoreByStato(newFeature.properties.statoRichiesta);

          // Assegna il colore alle proprietà della feature
          (newFeature.properties as ComunePropertiesWithColor).fillColor = colore;
          // Aggiungiamo anche il nome dello stato corrispondente per il popup
          (newFeature.properties as ComunePropertiesWithColor).statoColore = newFeature.properties.statoRichiesta;

          return newFeature;
        });

        // Crea una nuova collezione con i colori assegnati
        const geoJsonColorato = {
          type: this.comuniData.type,
          features: features
        };

        // Aggiungi la fonte con i dati modificati
        (this.map as any).addSource('comuni-source', {
          type: 'geojson',
          data: geoJsonColorato
        });

        // Aggiungi il layer per i confini dei comuni
        (this.map as any).addLayer({
          id: 'comuni-confini',
          type: 'line',
          source: 'comuni-source',
          layout: {},
          paint: {
            'line-color': '#333333',
            'line-width': 1.5,
            'line-opacity': 0.8
          }
        });

        // Aggiungi il layer per il riempimento dei comuni con colori degli stati
        (this.map as any).addLayer({
          id: 'comuni-fill',
          type: 'fill',
          source: 'comuni-source',
          layout: {},
          paint: {
            'fill-color': ['get', 'fillColor'],
            'fill-opacity': 0.5
          }
        });

        // Aggiungi interattività
        this.aggiungiInterattivitaComuni();

        ////this.logger.info('[DASHBOARD] Confini comuni aggiunti con colori degli stati');
      }
    } catch (err) {
      this.logger.error('[DASHBOARD] Errore nell\'aggiunta dei confini comuni', err);
    }
  }

  // Metodo per ottenere il colore in base allo stato
  private getColoreByStato(stato: string): string {
    switch (stato) {
      case 'IN PREPARAZIONE':
        return '#2196F3';
      case 'PRESENTATA':
        return '#eabf51';
      case 'DA FINANZIARE':
        return '#9C27B0';
      case 'FINANZIATA':
        return '#00BCD4';
      case 'IN RENDICONTAZIONE':
        return '#009688';
      case 'RENDICONTATA':
        return '#4CAF50';
      case 'IN LIQUIDAZIONE':
        return '#FFA500';
      case 'LIQUIDATA':
        return '#008000';
      default:
        return '#808080'; // Grigio come fallback
    }
  }

  private aggiungiInterattivitaComuni(): void {
    if (!this.map) return;

    try {
      // Nascondi il tooltip quando il mouse esce completamente dalla mappa
      (this.map as any).on('mouseout', () => {
        if (!this.map || !this.mapTooltipEl) return;

        // Nascondi il tooltip
        this.renderer.removeClass(this.mapTooltipEl.nativeElement, 'visible');
        this.tooltipVisible = false;
      });

      // Utilizziamo mousemove per gestire sia l'entrata che l'uscita dai comuni
      // e gli aggiornamenti durante il movimento tra comuni
      this.map.on('mousemove', (e: any) => {
        if (!this.map || !this.mapTooltipEl) return;

        // Verifica se il mouse è su un comune
        const features = (this.map as any).queryRenderedFeatures(e.point, {
          layers: ['comuni-fill']
        });

        // Verifica se il mouse è su un marker dei consorzi
        const isOverMarker = this.markers.some(marker => {
          const markerEl = marker.getElement();
          const rect = markerEl.getBoundingClientRect();
          const mapRect = this.mapContainer.nativeElement.getBoundingClientRect();
          const markerX = rect.left - mapRect.left;
          const markerY = rect.top - mapRect.top;
          const markerWidth = rect.width;
          const markerHeight = rect.height;

          return e.point.x >= markerX &&
                 e.point.x <= markerX + markerWidth &&
                 e.point.y >= markerY &&
                 e.point.y <= markerY + markerHeight;
        });

        // Se il mouse è su un marker, non mostrare il tooltip dei comuni
        if (isOverMarker) {
          this.renderer.removeClass(this.mapTooltipEl.nativeElement, 'visible');
          this.tooltipVisible = false;
          return;
        }

        // Aggiorna il cursore in base alla presenza di feature
        (this.map as any).getCanvas().style.cursor = features.length ? 'pointer' : '';

        if (features.length > 0) {
          // Il mouse è su un comune, ottieni le proprietà
          const feature = features[0];
          const props = feature.properties;

          // Aggiorna contenuto del tooltip
          const tooltipEl = this.mapTooltipEl.nativeElement;
          const titleEl = tooltipEl.querySelector('#tooltip-title');
          const statoEl = tooltipEl.querySelector('#tooltip-stato');

          if (titleEl && statoEl) {
            titleEl.textContent = props.denominazione;
            statoEl.textContent = props.statoColore;
            statoEl.style.color = props.fillColor;
          }

          // Mostra il tooltip se non è già visibile
          if (!this.tooltipVisible) {
            this.renderer.addClass(tooltipEl, 'visible');
            this.tooltipVisible = true;
          }

          // Aggiorna la posizione del tooltip
          this.aggiornaPosizioneTooltip(e.point);
        } else {
          // Il mouse non è su nessun comune, nascondi il tooltip
          if (this.tooltipVisible) {
            this.renderer.removeClass(this.mapTooltipEl.nativeElement, 'visible');
            this.tooltipVisible = false;
          }
        }
      });

      // Popup al click su un comune - Mostra un toast
      (this.map as any).on('click', 'comuni-fill', (e: any) => {
        if (!this.map) return;

        // Estrai le proprietà del comune
        const comune = e.features[0];
        const props = comune.properties;
        this.apriInterventiComune(props.codiceCatastale, props.denominazione);
        // Mostra messaggio toast
       /*  this.messageService.add({
          severity: 'info',
          summary: props.denominazione,
          detail: `Stato: ${props.statoColore} - Codice: ${props.codiceCatastale}`,
          life: 3000,
          styleClass: 'comune-toast',
          contentStyleClass: 'comune-toast-content'
        });
 */
       // //this.logger.info(`[DASHBOARD] Click su comune: ${props.denominazione} - Stato: ${props.st} - Codice: ${props.codiceCatastale}`);
      });
    } catch (error) {
      this.logger.error('[DASHBOARD] Errore nell\'aggiunta dell\'interattività:', error);
    }
  }

  /**
   * Aggiorna la posizione del tooltip in base alla posizione del mouse
   */
  private aggiornaPosizioneTooltip(point: { x: number; y: number }): void {
    if (!this.mapTooltipEl) return;

    const tooltipEl = this.mapTooltipEl.nativeElement;

    // Posiziona il tooltip sopra il punto
    this.renderer.setStyle(tooltipEl, 'left', `${point.x}px`);
    this.renderer.setStyle(tooltipEl, 'top', `${point.y}px`);
    this.renderer.setStyle(tooltipEl, 'transform', 'translate(-50%, -100%)');
    this.renderer.setStyle(tooltipEl, 'z-index', '3000'); // Aumentiamo il valore da 1000 a 3000
    this.renderer.setStyle(tooltipEl, 'pointer-events', 'none');
  }

  // Cleanup quando il componente viene distrutto
  ngOnDestroy(): void {
    // Pulisci sottoscrizioni
    if (this.comuniSubscription) {
      this.comuniSubscription.unsubscribe();
    }

    // Rimuovi tooltip e popup
    if (this.mapTooltipEl && this.tooltipVisible) {
      this.renderer.removeClass(this.mapTooltipEl.nativeElement, 'visible');
    }

    if (this.popupAttivo) {
      this.popupAttivo.remove();
    }

    // Rimuovi i marker
    this.markers.forEach(marker => marker.remove());

    // Rimuovi mappa
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }
}
